import { Creator, CreatorListResponse } from '../types/creators';
import {
  Category,
  DeliveryEstimation,
  DeliveryEstimationRequest,
  Material,
  PriceCalculation,
  PriceCalculationRequest,
  Product,
  ProductListResponse,
} from '../types/product';
import { BaseApiService } from './baseApiService';
import cacheService from './cacheService';

/**
 * Service for product and creator operations
 */
class ProductService extends BaseApiService {
  async getProducts(params?: {
    searchTerm?: string;
    category?: string;
    material?: string;
    creator?: string;
    minPrice?: number;
    maxPrice?: number;
    isFeatured?: boolean;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    pageSize?: number;
    deliveryCountry?: string;
  }) {
    await this.ensureInitialized();
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const response = await this.api.get(`/products?${searchParams.toString()}`);
    return response.data;
  }

  async getProduct(id: string, deliveryCountry?: string) {
    await this.ensureInitialized();
    const params = deliveryCountry
      ? `?deliveryCountry=${encodeURIComponent(deliveryCountry)}`
      : '';

    if (deliveryCountry) {
      // Bypass cache when filtering by delivery - API may return 404
      const response = await this.api.get(`/products/${id}${params}`);
      return response.data;
    }

    const cacheResult = await cacheService.getProduct(id, async () => {
      const response = await this.api.get(`/products/${id}`);
      return response.data;
    });

    return cacheResult.data;
  }

  async getFeaturedProducts(deliveryCountry?: string): Promise<Product[]> {
    await this.ensureInitialized();
    const params = deliveryCountry
      ? `?deliveryCountry=${encodeURIComponent(deliveryCountry)}`
      : '';
    const cacheResult = await cacheService.getFeaturedProducts(async () => {
      const response = await this.api.get(`/products/featured${params}`);
      return response.data;
    });

    return cacheResult.data || [];
  }

  async getCategories(): Promise<Category[]> {
    await this.ensureInitialized();
    const response = await this.api.get('/products/categories');
    return response.data;
  }

  async getMaterials(): Promise<Material[]> {
    await this.ensureInitialized();
    const response = await this.api.get('/products/materials');
    return response.data;
  }

  async getCreators(): Promise<Creator[]> {
    await this.ensureInitialized();
    const response = await this.api.get('/creator/featured');
    return response.data || [];
  }

  async getAllCreators(
    page: number = 1,
    pageSize: number = 20,
    isFeatured?: boolean
  ): Promise<CreatorListResponse> {
    await this.ensureInitialized();
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (isFeatured !== undefined) {
      params.append('isFeatured', isFeatured.toString());
    }

    const response = await this.api.get(`/creator?${params}`);
    return response.data;
  }

  async getCreatorById(id: string): Promise<Creator> {
    await this.ensureInitialized();
    const response = await this.api.get(`/creator/${id}`);
    return response.data;
  }

  async getCreatorProducts(
    id: string,
    page: number = 1,
    pageSize: number = 20,
    deliveryCountry?: string
  ): Promise<ProductListResponse> {
    await this.ensureInitialized();
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (deliveryCountry) params.append('deliveryCountry', deliveryCountry);

    const response = await this.api.get(`/creator/${id}/products?${params}`);
    return response.data;
  }

  async getProductsByCategory(category: string, deliveryCountry?: string) {
    await this.ensureInitialized();
    const params = deliveryCountry
      ? `?deliveryCountry=${encodeURIComponent(deliveryCountry)}`
      : '';
    const response = await this.api.get(
      `/products/category/${encodeURIComponent(category)}${params}`
    );
    return response.data;
  }

  async getProductsByMaterial(material: string, deliveryCountry?: string) {
    await this.ensureInitialized();
    const params = deliveryCountry
      ? `?deliveryCountry=${encodeURIComponent(deliveryCountry)}`
      : '';
    const response = await this.api.get(
      `/products/material/${encodeURIComponent(material)}${params}`
    );
    return response.data;
  }

  async getProductsByCreator(creator: string, deliveryCountry?: string) {
    await this.ensureInitialized();
    const params = deliveryCountry
      ? `?deliveryCountry=${encodeURIComponent(deliveryCountry)}`
      : '';
    const response = await this.api.get(
      `/products/creator/${encodeURIComponent(creator)}${params}`
    );
    return response.data;
  }

  async searchProducts(query: string, deliveryCountry?: string) {
    await this.ensureInitialized();
    const params = new URLSearchParams();
    if (deliveryCountry) params.append('deliveryCountry', deliveryCountry);
    const paramStr = params.toString() ? `?${params}` : '';
    const response = await this.api.get(
      `/products/search/${encodeURIComponent(query)}${paramStr}`
    );
    return response.data;
  }

  async calculatePrice(
    request: PriceCalculationRequest
  ): Promise<PriceCalculation> {
    await this.ensureInitialized();
    const response = await this.api.post('/products/calculate-price', request);
    return response.data;
  }

  async getProductStartingPrice(productId: string): Promise<number> {
    await this.ensureInitialized();
    const response = await this.api.get(
      `/products/${productId}/starting-price`
    );
    return response.data;
  }

  async estimateDelivery(
    request: DeliveryEstimationRequest
  ): Promise<DeliveryEstimation> {
    await this.ensureInitialized();
    const response = await this.api.post(
      '/products/estimate-delivery',
      request
    );
    return response.data;
  }

  async getProductDeliveryEstimate(
    productId: string,
    materialId?: string,
    extraIds?: string[],
    quantity: number = 1
  ): Promise<DeliveryEstimation> {
    await this.ensureInitialized();
    const request: DeliveryEstimationRequest = {
      productId,
      selectedMaterialId: materialId,
      selectedExtraIds: extraIds || [],
      quantity,
    };

    const response = await this.api.post(
      '/products/estimate-delivery',
      request
    );
    return response.data;
  }
}

export const productService = new ProductService();
export default productService;
